import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Mail,
  Printer,
  Trash2,
  X,
  Plus,
  ExternalLink,
  Phone,
  Pencil,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { printPdf } from '@/lib/printPdf';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatCents, formatDate, todayISO } from '@/lib/utils';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import EmailPdfModal from '@/components/EmailPdfModal';

// ─── Types ───────────────────────────────────────────────────────────────────

type PRStatus = 'open' | 'refunded';

interface PRItem {
  id: number;
  item_id: number | null;
  description: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

interface PRReceipt {
  id: number;
  amount: number;
  date: string;
  notes: string | null;
  created_by: number | null;
  created_at: string;
}

interface PRDetail {
  id: number;
  number: string;
  po_id: number;
  supplier_id: number;
  date: string;
  status: PRStatus;
  total: number;
  notes: string | null;
}

interface PRDetailResponse {
  data: {
    purchase_return: PRDetail;
    items: PRItem[];
    supplier: {
      id: number;
      name: string;
      contact_person: string | null;
      phone: string | null;
      email: string | null;
      address: string | null;
    };
    po: { id: number; number: string };
    receipts: PRReceipt[];
    supplier_credit_issued: number;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function prStatusBadge(status: PRStatus) {
  if (status === 'refunded') return <Badge variant="green">Refunded</Badge>;
  return <Badge variant="amber">Open</Badge>;
}

// ─── Record Receipt Modal ─────────────────────────────────────────────────────

interface RecordReceiptModalProps {
  prId: number;
  balanceCents: number;
  onClose: () => void;
  onSaved: () => void;
}

function RecordReceiptModal({ prId, balanceCents, onClose, onSaved }: RecordReceiptModalProps) {
  const prefill = (Math.max(balanceCents, 0) / 100).toFixed(2);
  const [amountStr, setAmountStr] = useState(prefill);
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/purchase-returns/${prId}/receipt`, {
        method: 'POST',
        body: JSON.stringify({ amount, date, notes: notes.trim() || undefined }),
      });
      toast.success('Receipt recorded');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Record Receipt</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="rcpt-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="rcpt-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rcpt-date">Date</Label>
              <Input
                id="rcpt-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rcpt-notes">Notes (optional)</Label>
            <Input
              id="rcpt-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash received"
              disabled={loading}
            />
          </div>
          {balanceCents > 0 && (
            <p className="text-xs text-muted-foreground">
              Balance to settle:{' '}
              <span className="font-medium tabular-nums">{formatCents(balanceCents)}</span>
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Record Receipt'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit Receipt Modal ───────────────────────────────────────────────────────

interface EditReceiptModalProps {
  receipt: PRReceipt;
  onClose: () => void;
  onSaved: () => void;
}

function EditReceiptModal({ receipt, onClose, onSaved }: EditReceiptModalProps) {
  const [amountStr, setAmountStr] = useState(String((receipt.amount / 100).toFixed(2)));
  const [date, setDate] = useState(receipt.date.slice(0, 10));
  const [notes, setNotes] = useState(receipt.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/purchase-receipts/${receipt.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ amount, date, notes: notes.trim() || undefined }),
      });
      toast.success('Receipt updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Edit Receipt</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="er-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="er-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="er-date">Date</Label>
              <Input
                id="er-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="er-notes">Notes (optional)</Label>
            <Input
              id="er-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash received"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Issue Supplier Credit Modal ──────────────────────────────────────────────

interface IssueSupplierCreditModalProps {
  prId: number;
  supplierId: number;
  poId: number;
  maxCents: number;
  onClose: () => void;
  onSaved: () => void;
}

function IssueSupplierCreditModal({
  prId,
  supplierId,
  poId,
  maxCents,
  onClose,
  onSaved,
}: IssueSupplierCreditModalProps) {
  const queryClient = useQueryClient();
  const prefill = (Math.max(maxCents, 0) / 100).toFixed(2);
  const [amountStr, setAmountStr] = useState(prefill);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/purchase-returns/${prId}/supplier-credit`, {
        method: 'POST',
        body: JSON.stringify({ amount, notes: notes.trim() || undefined }),
      });
      toast.success('Supplier credit issued');
      queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(supplierId)] });
      queryClient.invalidateQueries({ queryKey: ['purchase-order', String(poId)] });
      onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      toast.error(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Issue Supplier Credit</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sc-amount">
              Amount ($) <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                $
              </span>
              <Input
                id="sc-amount"
                type="number"
                min="0.01"
                max={(Math.max(maxCents, 0) / 100).toFixed(2)}
                step="0.01"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                className="pl-7 tabular-nums"
                required
                disabled={loading}
              />
            </div>
            {maxCents > 0 && (
              <p className="text-xs text-muted-foreground">
                Balance to settle:{' '}
                <span className="font-medium tabular-nums">{formatCents(maxCents)}</span>
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sc-notes">Notes (optional)</Label>
            <Input
              id="sc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Supplier credit in lieu of cash"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Issuing…' : 'Issue Supplier Credit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm Delete Dialog ────────────────────────────────────────────────────

interface ConfirmDeleteProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

function ConfirmDeleteDialog({ title, message, onConfirm, onCancel, loading }: ConfirmDeleteProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PurchaseReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Modal state ──
  const [recordReceiptOpen, setRecordReceiptOpen] = useState(false);
  const [issueSupplierCreditOpen, setIssueSupplierCreditOpen] = useState(false);
  const [editReceipt, setEditReceipt] = useState<PRReceipt | null>(null);
  const [deleteReceiptId, setDeleteReceiptId] = useState<number | null>(null);
  const [deletingReceipt, setDeletingReceipt] = useState(false);
  const [deletePROpen, setDeletePROpen] = useState(false);
  const [deletingPR, setDeletingPR] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['purchase-return', id],
    queryFn: () => apiFetch<PRDetailResponse>(`/api/purchase-returns/${id}`),
    enabled: !!id,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['purchase-return', id] });
    queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
    if (data?.data.purchase_return) {
      const pr = data.data.purchase_return;
      queryClient.invalidateQueries({ queryKey: ['purchase-order', String(pr.po_id)] });
      queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(pr.supplier_id)] });
    }
  }

  // ── Delete PR ──
  async function handleDeletePR() {
    setDeletingPR(true);
    try {
      await apiFetch(`/api/purchase-returns/${id}`, { method: 'DELETE' });
      toast.success('Purchase return deleted');
      queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
      if (data?.data.purchase_return) {
        const pr = data.data.purchase_return;
        queryClient.invalidateQueries({ queryKey: ['purchase-order', String(pr.po_id)] });
        queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
        queryClient.invalidateQueries({ queryKey: ['supplier-ledger', String(pr.supplier_id)] });
      }
      navigate('/purchase-returns');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete purchase return');
    } finally {
      setDeletingPR(false);
      setDeletePROpen(false);
    }
  }

  // ── Delete receipt ──
  async function handleDeleteReceipt(receiptId: number) {
    setDeletingReceipt(true);
    try {
      await apiFetch(`/api/purchase-receipts/${receiptId}`, { method: 'DELETE' });
      toast.success('Receipt deleted');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete receipt');
    } finally {
      setDeletingReceipt(false);
      setDeleteReceiptId(null);
    }
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (error || !data?.data.purchase_return) {
    return (
      <div className="p-6">
        <Link
          to="/purchase-returns"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Purchase Returns
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Purchase return not found.'}
        </div>
      </div>
    );
  }

  const { purchase_return: pr, items, supplier, po, receipts, supplier_credit_issued } = data.data;
  const cashReceived = receipts.reduce((sum, r) => sum + r.amount, 0);
  const supplierCreditIssued = supplier_credit_issued ?? 0;
  const balanceCents = pr.total - cashReceived - supplierCreditIssued;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* ── Back nav ── */}
      <Link
        to="/purchase-returns"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Purchase Returns
      </Link>

      {/* ── Body: 60/40 grid — header + returned items in the 60% main column ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Main column (60%) */}
        <div className="lg:col-span-3 space-y-6">
          {/* ── Header card ── */}
          <Card>
            <div className="px-6 py-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                {/* Left: number, status, supplier, PO, date */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold tracking-tight tabular-nums">{pr.number}</h1>
                    {prStatusBadge(pr.status)}
                    {isAdmin && pr.status === 'open' && (
                      <Link to={`/purchase-returns/${pr.id}/edit`}>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
                    <Link
                      to={`/suppliers/${supplier.id}`}
                      className="font-medium text-foreground hover:underline inline-flex items-center gap-1"
                    >
                      {supplier.name}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </Link>
                    {supplier.phone && (
                      <>
                        <span className="hidden sm:inline text-muted-foreground">·</span>
                        <a
                          href={`tel:${supplier.phone}`}
                          className="text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
                        >
                          <Phone className="h-3 w-3" />
                          {supplier.phone}
                        </a>
                      </>
                    )}
                    <span className="hidden sm:inline text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                      Against PO:{' '}
                      <Link
                        to={`/purchase-orders/${po.id}`}
                        className="font-medium text-foreground hover:underline"
                      >
                        {po.number}
                      </Link>
                    </span>
                    <span className="hidden sm:inline text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{formatDate(pr.date)}</span>
                  </div>
                  {pr.notes && (
                    <p className="text-sm text-muted-foreground max-w-prose whitespace-pre-line">
                      {pr.notes}
                    </p>
                  )}
                </div>

                {/* Right: action buttons */}
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printPdf(`/api/purchase-returns/${id}/pdf`)}
                  >
                    <Printer className="h-3.5 w-3.5 mr-1.5" />
                    Print
                  </Button>
                  <a
                    href={`/api/purchase-returns/${id}/pdf?download=1`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download PDF
                  </a>
                  <Button variant="outline" size="sm" onClick={() => setEmailModalOpen(true)}>
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    Email
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setDeletePROpen(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* ── Returned items + totals ── */}
          <Card>
            <div className="px-4 py-3 border-b bg-muted/30">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Returned Items
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground w-8">
                      #
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                      Description
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-20">
                      Qty
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Unit Price
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Line Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr
                      key={item.id}
                      className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-3 py-2.5 text-xs text-muted-foreground text-center">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-medium">
                        {item.item_id != null ? (
                          <Link
                            to={`/items/${item.item_id}`}
                            className="inline-block hover:underline hover:text-primary transition-colors"
                          >
                            {item.description}
                          </Link>
                        ) : (
                          item.description
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm">{item.qty}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                        {formatCents(item.unit_price)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                        {formatCents(item.line_total)}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-sm text-muted-foreground"
                      >
                        No line items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Settlement summary block */}
            <div className="px-4 py-4 border-t bg-muted/10">
              <div className="ml-auto max-w-xs space-y-1.5">
                <div className="flex items-center justify-between pt-2 border-t font-semibold">
                  <span>Return Total</span>
                  <span className="tabular-nums text-base">{formatCents(pr.total)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Cash Received</span>
                  <span className="tabular-nums text-green-700 dark:text-green-400">
                    {formatCents(cashReceived)}
                  </span>
                </div>
                {supplierCreditIssued > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Supplier Credit Issued</span>
                    <span className="tabular-nums text-blue-700 dark:text-blue-400">
                      {formatCents(supplierCreditIssued)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm font-medium pt-1 border-t">
                  <span>Balance to Settle</span>
                  <span
                    className={`tabular-nums ${balanceCents > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}
                  >
                    {formatCents(Math.max(balanceCents, 0))}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* ── Receipts / Settlement section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Receipts &amp; Settlement
                </h2>
                {balanceCents > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIssueSupplierCreditOpen(true)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Issue Supplier Credit
                    </Button>
                    <Button size="sm" onClick={() => setRecordReceiptOpen(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Record Receipt
                    </Button>
                  </div>
                )}
              </div>

              {receipts.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No receipts recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Amount
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Notes
                        </th>
                        {isAdmin && <th className="px-3 py-2.5 w-20" />}
                      </tr>
                    </thead>
                    <tbody>
                      {receipts.map((rcpt) => (
                        <tr
                          key={rcpt.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-3 py-2.5 tabular-nums text-sm">
                            {formatDate(rcpt.date)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium text-green-700 dark:text-green-400">
                            {formatCents(rcpt.amount)}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-muted-foreground">
                            {rcpt.notes ?? '—'}
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => setEditReceipt(rcpt)}
                                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                  title="Edit receipt"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteReceiptId(rcpt.id)}
                                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  title="Delete receipt"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar (40%) */}
        <aside className="lg:col-span-2 space-y-6">
          {/* ── Parent PO ── */}
          <Card>
            <div className="px-4 py-3 border-b bg-muted/30">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Purchase Order
              </h2>
            </div>
            <CardContent className="p-4">
              <Link
                to={`/purchase-orders/${po.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:underline"
              >
                {po.number}
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">
                This return was made against the above purchase order.
              </p>
            </CardContent>
          </Card>

          {/* ── Attachments / Notes ── */}
          <AttachmentsPanel module="purchase_return" recordId={Number(id)} />
        </aside>
      </div>

      {/* ── Modals ── */}
      {recordReceiptOpen && (
        <RecordReceiptModal
          prId={pr.id}
          balanceCents={balanceCents}
          onClose={() => setRecordReceiptOpen(false)}
          onSaved={invalidate}
        />
      )}

      {issueSupplierCreditOpen && (
        <IssueSupplierCreditModal
          prId={pr.id}
          supplierId={pr.supplier_id}
          poId={pr.po_id}
          maxCents={Math.max(balanceCents, 0)}
          onClose={() => setIssueSupplierCreditOpen(false)}
          onSaved={invalidate}
        />
      )}

      {editReceipt && (
        <EditReceiptModal
          receipt={editReceipt}
          onClose={() => setEditReceipt(null)}
          onSaved={invalidate}
        />
      )}

      {deleteReceiptId !== null && (
        <ConfirmDeleteDialog
          title="Delete Receipt"
          message="Are you sure you want to delete this receipt? This action cannot be undone."
          onConfirm={() => handleDeleteReceipt(deleteReceiptId)}
          onCancel={() => setDeleteReceiptId(null)}
          loading={deletingReceipt}
        />
      )}

      {deletePROpen && (
        <ConfirmDeleteDialog
          title="Delete Purchase Return"
          message={`Are you sure you want to delete purchase return ${pr.number}? This reverses stock and removes receipts. This cannot be undone.`}
          onConfirm={handleDeletePR}
          onCancel={() => setDeletePROpen(false)}
          loading={deletingPR}
        />
      )}

      <EmailPdfModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        endpoint={`/api/purchase-returns/${id}/email`}
        defaultTo={supplier.email ?? ''}
        defaultSubject={`Purchase Return ${pr.number}`}
        pdfHref={`/api/purchase-returns/${id}/pdf`}
      />
    </div>
  );
}
